import { useCallback, useEffect, useState } from "preact/hooks";

import { ApiError, api } from "./api";

interface Result<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Adatlekérés újratöltéssel.
 *
 * A megszakítás fontos: a nézetváltás közben befutó válasz különben egy
 * lecsatolt komponensbe írna, és a felhasználó a RÉGI nézet adatát látná
 * villanni az újban.
 */
export function useApi<T>(path: string | null, deps: unknown[] = []): Result<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!path) return;
    let elavult = false;
    setLoading(true);
    setError(null);

    api
      .get<T>(path)
      .then((result) => {
        if (!elavult) setData(result);
      })
      .catch((e: unknown) => {
        if (elavult) return;
        // A 401-et a kliens már kezelte (kilépteti a felhasználót),
        // itt nem írunk fölé hibaüzenetet.
        if (e instanceof ApiError && e.status === 401) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!elavult) setLoading(false);
      });

    return () => {
      elavult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  return { data, error, loading, reload };
}
